// components/Publications.jsx
import axios from "axios";
import React, { useEffect, useState } from "react";

const Publications = () => {
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    title_ar: "",
    author: "",
    author_ar: "",
    year: "",
    file: null,
  });
  const [filePreview, setFilePreview] = useState(null);
  const [flag, setFlag] = useState(false);
  const [publications, setPublications] = useState([
    {
      id: 1,
      title: "Advanced Machine Learning Techniques",
      author: "Dr. Sarah Johnson",
      year: "2023",
      file: "/api/placeholder/document.pdf",
      fileType: "pdf",
      createdAt: "2024-01-15",
      downloads: 245,
      size: "2.4 MB",
    },
    {
      id: 2,
      title: "Modern Web Development Patterns",
      author: "Michael Chen",
      year: "2023",
      file: "/api/placeholder/document.docx",
      fileType: "docx",
      createdAt: "2024-01-10",
      downloads: 189,
      size: "1.8 MB",
    },
    {
      id: 3,
      title: "Sustainable Energy Solutions",
      author: "Dr. Emily Rodriguez",
      year: "2022",
      file: "/api/placeholder/document.pdf",
      fileType: "pdf",
      createdAt: "2024-01-05",
      downloads: 312,
      size: "3.1 MB",
    },
    {
      id: 4,
      title: "Data Analytics in Business",
      author: "Prof. James Wilson",
      year: "2024",
      file: "/api/placeholder/document.docx",
      fileType: "docx",
      createdAt: "2024-01-01",
      downloads: 156,
      size: "2.1 MB",
    },
  ]);
  const [file, setFile] = useState(null);

  // Handle Add/Edit Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditing && editingPublication) {
      // Update existing publication
      const formDataRequest = new FormData();
      formDataRequest.append("publication_id", editingPublication.id);
      formDataRequest.append("title", formData.title);
      formDataRequest.append("title_ar", formData.title_ar);
      formDataRequest.append("author", formData.author);
      formDataRequest.append("author_ar", formData.author_ar);
      formDataRequest.append("year", formData.year);
      if (file) {
        formDataRequest.append(
          "fileType",
          file ? file.name.split(".").pop() : "pdf"
        );
        formDataRequest.append(
          "size",
          file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : "0 MB"
        );
        formDataRequest.append("file", file);
      }
      await axios.patch(
        process.env.REACT_APP_BASE_URL + "publication/update-publication",
        formDataRequest
      );
      setFlag(!flag);
    } else {
      // Add new publication
      const formDataRequest = new FormData();
      formDataRequest.append("title", formData.title);
      formDataRequest.append("title_ar", formData.title_ar);
      formDataRequest.append("author", formData.author);
      formDataRequest.append("author_ar", formData.author_ar);
      formDataRequest.append("year", formData.year);
      formDataRequest.append(
        "fileType",
        formData.file ? formData.file.name.split(".").pop() : "pdf"
      );
      formDataRequest.append(
        "size",
        formData.file
          ? `${(formData.file.size / 1024 / 1024).toFixed(1)} MB`
          : "0 MB"
      );
      formDataRequest.append("file", formData.file);

      await axios.post(
        process.env.REACT_APP_BASE_URL + "publication/add-publication",
        formDataRequest
      );
      setFlag(!flag);
    }

    resetForm();
  };

  // Handle Edit
  const handleEdit = (publication) => {
    setFormData({
      title: publication.title,
      title_ar: publication.title_ar,
      author: publication.author,
      author_ar: publication.author_ar,
      year: publication.year,
      file: null,
    });
    setFilePreview(publication.file);
    setEditingPublication(publication);
    setIsEditing(true);
    setIsAdding(true);
  };

  // Handle Delete
  const handleDelete = async (publicationId) => {
    // setPublications(publications.filter((p) => p.id !== publicationId));
    await axios.delete(
      process.env.REACT_APP_BASE_URL + "publication/delete-publication",
      {
        params: {
          publication_id: publicationId,
        },
      }
    );
    setFlag(!flag);
  };

  // Reset form and states
  const resetForm = () => {
    setFormData({ title: "", author: "", year: "", file: null });
    setFilePreview(null);
    setIsAdding(false);
    setIsEditing(false);
    setEditingPublication(null);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "file" && files.length > 0) {
      const file = files[0];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (fileExtension !== "pdf" && fileExtension !== "docx") {
        alert("Please upload only PDF or DOCX files");
        return;
      }

      if (isEditing) {
        setFile(file);
      } else {
        setFormData({
          ...formData,
          file: file,
        });
      }

      // Create preview (for demo, we'll just show an icon)
      setFilePreview(URL.createObjectURL(file));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    return fileType === "pdf" ? "📄" : "📝";
  };

  // Get file type color
  const getFileColor = (fileType) => {
    return fileType === "pdf"
      ? "from-green-500 to-green-600"
      : "from-green-400 to-green-500";
  };

  // Get file type badge
  const getFileBadge = (fileType) => {
    return fileType === "pdf"
      ? { color: "bg-red-100 text-red-800", text: "PDF" }
      : { color: "bg-blue-100 text-blue-800", text: "DOCX" };
  };

  // Get current year for max year input
  const getCurrentYear = () => {
    return new Date().getFullYear().toString();
  };

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_BASE_URL + "publication/get-all-publications")
      .then((res) => setPublications(res.data.data));
  }, [flag]);

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Publications</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {publications.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">📚</span>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Downloads</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {publications.reduce((sum, pub) => sum + pub.downloads, 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">⬇️</span>
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">PDF Files</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {publications.filter((pub) => pub.fileType === "pdf").length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">📄</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 transform hover:scale-105 transition-all duration-300 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">DOCX Files</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">
                {publications.filter((pub) => pub.fileType === "docx").length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">📝</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Publications</h2>
          <p className="text-gray-600 mt-1">
            Manage your research papers, articles, and documents
          </p>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 group"
        >
          <span className="group-hover:scale-110 transition-transform duration-200">
            +
          </span>
          <span>Add Publication</span>
        </button>
      </div>

      {/* Add/Edit Publication Form Modal */}
      {isAdding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl transform animate-scale-in max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-green-200 bg-gradient-to-r from-green-50 to-green-100 rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-800">
                {isEditing ? "Edit Publication" : "Add New Publication"}
              </h3>
              <p className="text-green-600 text-sm mt-1">
                {isEditing
                  ? "Update the publication details"
                  : "Upload a new research paper or document"}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document File *
                </label>
                <div className="flex items-center space-x-6">
                  <div className="w-32 h-40 bg-green-100 rounded-lg border-2 border-dashed border-green-300 flex items-center justify-center overflow-hidden">
                    {filePreview ? (
                      <div className="text-center">
                        <span className="text-4xl text-green-600">
                          {formData.file?.name?.endsWith(".pdf") ? "📄" : "📝"}
                        </span>
                        <p className="text-xs text-green-700 mt-2">
                          {formData.file?.name}
                        </p>
                      </div>
                    ) : (
                      <span className="text-green-400 text-3xl">📄</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      name="file"
                      accept=".pdf,.docx"
                      onChange={handleChange}
                      required={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Upload PDF or DOCX files only (Max: 10MB)
                    </p>
                  </div>
                </div>
              </div>

              {/* Title Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publication Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter publication title"
                />
              </div>

              {/* Title Arabic Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publication Title Arabic *
                </label>
                <input
                  type="text"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter publication title"
                />
              </div>

              {/* Author Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter author name"
                />
              </div>

              {/* Author Arabic Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author Arabic *
                </label>
                <input
                  type="text"
                  name="author_ar"
                  value={formData.author_ar}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter author name"
                />
              </div>

              {/* Year Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publication Year *
                </label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  required
                  min="1900"
                  max={getCurrentYear()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                  placeholder="Enter publication year"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  {isEditing ? "Update Publication" : "Add Publication"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Publications Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {publications.map((publication, index) => {
          const fileBadge = getFileBadge(publication.fileType);
          return (
            <div
              key={publication.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300 group animate-fade-in border border-green-100"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Publication Header */}
              <div
                className={`bg-gradient-to-r ${getFileColor(
                  publication.fileType
                )} p-6 text-white relative overflow-hidden`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">
                      {getFileIcon(publication.fileType)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {publication.downloads}
                    </p>
                    <p className="text-sm opacity-90">Downloads</p>
                  </div>
                </div>
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
                  <button
                    onClick={() => handleEdit(publication)}
                    className="w-8 h-8 bg-white text-green-600 rounded-full flex items-center justify-center hover:bg-green-50 transition-colors duration-200 transform hover:scale-110 shadow-sm"
                    title="Edit Publication"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(publication.id)}
                    className="w-8 h-8 bg-white text-red-600 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors duration-200 transform hover:scale-110 shadow-sm"
                    title="Delete Publication"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
                <div className="absolute top-3 left-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${fileBadge.color}`}
                  >
                    {fileBadge.text}
                  </span>
                </div>
              </div>

              {/* Publication Details */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-800 mb-2 group-hover:text-green-700 transition-colors duration-200 line-clamp-2">
                  {publication.title}
                </h3>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="font-medium">{publication.author}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>Published: {publication.year}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    <span>Size: {publication.size}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>Added {formatDate(publication.createdAt)}</span>
                  <span className="flex items-center space-x-1">
                    <span>⬇️</span>
                    <span>{publication.downloads} downloads</span>
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2">
                  <button className="flex-1 bg-green-50 text-green-600 py-2 px-3 rounded-lg font-medium hover:bg-green-100 transition-colors duration-200 flex items-center justify-center space-x-1 text-sm">
                    <span>👁️</span>
                    <span>Preview</span>
                  </button>
                  <button className="flex-1 bg-blue-50 text-blue-600 py-2 px-3 rounded-lg font-medium hover:bg-blue-100 transition-colors duration-200 flex items-center justify-center space-x-1 text-sm">
                    <span>⬇️</span>
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add New Publication Card */}
        <div
          className="border-2 border-dashed border-green-300 rounded-2xl p-8 text-center hover:border-green-500 hover:bg-green-50 transition-all duration-300 cursor-pointer group min-h-[300px] flex items-center justify-center"
          onClick={() => setIsAdding(true)}
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors duration-200">
            <span className="text-2xl text-green-600 group-hover:scale-110 transition-transform duration-200">
              +
            </span>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2 group-hover:text-green-700 transition-colors duration-200">
            Add New Publication
          </h3>
          <p className="text-gray-500 text-sm group-hover:text-gray-600 transition-colors duration-200">
            Upload research papers, articles, or documents
          </p>
        </div>
      </div>

      {/* Empty State */}
      {publications.length === 0 && (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl text-green-600">📚</span>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No publications yet
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Start building your publications library by uploading research
            papers, articles, and documents in PDF or DOCX format.
          </p>
          <button
            onClick={() => setIsAdding(true)}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            Upload First Publication
          </button>
        </div>
      )}

      {/* Quick Stats Footer */}
      {publications.length > 0 && (
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm opacity-90">Total Publications</p>
              <p className="text-2xl font-bold">{publications.length}</p>
            </div>
            {/* <div>
              <p className="text-sm opacity-90">Total Downloads</p>
              <p className="text-2xl font-bold">
                {publications.reduce((sum, pub) => sum + pub.downloads, 0)}
              </p>
            </div> */}
            <div>
              <p className="text-sm opacity-90">PDF Files</p>
              <p className="text-2xl font-bold">
                {publications.filter((pub) => pub.fileType === "pdf").length}
              </p>
            </div>
            <div>
              <p className="text-sm opacity-90">DOCX Files</p>
              <p className="text-2xl font-bold">
                {publications.filter((pub) => pub.fileType === "docx").length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Publications;
